import type { ProblemType, LeafType, BankProblem, Answer } from '../types';
import { BANK } from '../data/bank';
import { MIX_COMPOSITION } from '../constants';

// Resolve a (possibly MIX) type down to a concrete leaf type to draw from.
// MIX_COMPOSITION only ever lists leaf types, so one hop is enough.
function resolveLeaf(type: ProblemType): LeafType {
  const members = (MIX_COMPOSITION as Partial<Record<ProblemType, LeafType[]>>)[type];
  if (!members || members.length === 0) return type as LeafType;
  return members[Math.floor(Math.random() * members.length)];
}

/**
 * Pick a problem from the static bank.
 * Avoids repeating `recentId` (the previously shown problem) when possible so
 * the child does not see the same question twice in a row.
 */
export function pickProblem(type: ProblemType, recentId?: string): BankProblem {
  const pool = BANK[resolveLeaf(type)];
  const candidates =
    pool.length > 1 && recentId ? pool.filter((p) => p.id !== recentId) : pool;
  const list = candidates.length > 0 ? candidates : pool;
  return list[Math.floor(Math.random() * list.length)];
}

// ---- answer checking ------------------------------------------------------

// Reject sloppy formats (leading zeros, trailing zeros/dot) before comparing.
function isWellFormed(input: string): boolean {
  if (/^0\d/.test(input)) return false; // 050
  if (input.includes('.') && /\..*0$/.test(input)) return false; // 1.20
  if (input.endsWith('.')) return false; // 5.
  return true;
}

export function checkNumberAnswer(input: string, answer: Answer): boolean {
  if (answer.kind !== 'number') return false;
  if (input === '' || !isWellFormed(input)) return false;
  return input === answer.display;
}

export function checkFractionAnswer(
  numerator: number,
  denominator: number,
  answer: Answer
): boolean {
  if (answer.kind !== 'fraction') return false;
  return numerator === answer.numerator && denominator === answer.denominator;
}
